"use client";

import * as React from "react";
import { Skeleton } from "./skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";

export interface TableSkeletonProps {
  /**
   * Number of skeleton rows to display
   * @default 5
   */
  rows?: number;
  /**
   * Number of columns
   * @default 5
   */
  columns?: number;
  /**
   * Whether to show pagination skeleton at the bottom
   * @default true
   */
  showPagination?: boolean;
  /**
   * Optional array of CSS width values for each column (e.g., ["100px", "200px", "auto"])
   * If not provided, columns will be evenly distributed
   */
  columnWidths?: string[];
  /**
   * Optional className for the container
   */
  className?: string;
}

export function TableSkeleton({
  rows = 5,
  columns = 5,
  showPagination = true,
  columnWidths,
  className,
}: TableSkeletonProps) {
  return (
    <div className={className}>
      <div className="rounded-xl border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {Array.from({ length: columns }).map((_, index) => (
                <TableHead
                  key={`header-${index}`}
                  style={
                    columnWidths?.[index]
                      ? { width: columnWidths[index] }
                      : undefined
                  }
                >
                  <Skeleton className="h-4 w-3/4" />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <TableRow key={`row-${rowIndex}`}>
                {Array.from({ length: columns }).map((_, colIndex) => (
                  <TableCell key={`cell-${rowIndex}-${colIndex}`}>
                    <Skeleton
                      className="h-4"
                      style={{
                        width:
                          columnWidths?.[colIndex] ||
                          `${Math.floor(Math.random() * 30) + 60}%`,
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {showPagination && (
        <div className="flex items-center justify-between px-2 py-4">
          <div className="flex items-center space-x-2">
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="flex items-center space-x-6 lg:space-x-8">
            <div className="flex items-center space-x-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-16" />
            </div>
            <div className="flex items-center space-x-2">
              <Skeleton className="h-4 w-32" />
            </div>
            <div className="flex items-center space-x-2">
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
